import { Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';
import { ThemeService } from '../../services/theme.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './login.component-v2.html',
  styleUrl: './login.component.css'
})
export class LoginComponent {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  protected readonly theme = inject(ThemeService);
  protected readonly hidePassword = signal(true);
  protected readonly passwordFieldType = computed(() => this.hidePassword() ? 'password' : 'text');
  protected readonly isSubmitting = signal(false);
  protected readonly loginError = signal('');
  protected readonly sessionNotice = this.getSessionNotice();

  protected readonly form = this.fb.nonNullable.group({
    userId: ['', [Validators.required]],
    password: ['', [Validators.required, Validators.minLength(4)]]
  });

  protected submit(): void {
    if (this.form.invalid || this.isSubmitting()) {
      this.form.markAllAsTouched();
      return;
    }

    this.loginError.set('');
    this.isSubmitting.set(true);

    const { userId, password } = this.form.getRawValue();

    this.auth.login({ userId, password })
      .pipe(
        // tap((session: any) => {
        //   console.log('Login successful:', session);
        // }), 
        finalize(() => {
          this.isSubmitting.set(false);

        }))
      .subscribe({
        next: async (session: any) => {
          const redirectUrl = this.route.snapshot.queryParamMap.get('redirect');
          await this.router.navigateByUrl(redirectUrl || this.auth.getDefaultRouteForRole(session.role));
        },
        error: (error: unknown) => {
          this.loginError.set(this.getErrorMessage(error));

        }
      });
  }

  protected togglePasswordVisibility(): void {
    this.hidePassword.update((value) => !value);
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }

    return 'Unable to sign in right now. Please try again.';
  }

  private getSessionNotice(): string {
    switch (this.route.snapshot.queryParamMap.get('reason')) {
      case 'session-expired':
        return 'Your session expired. Please sign in again.';
      case 'signed-out':
        return 'You have been signed out successfully.';
      default:
        return '';
    }
  }
}
